/**
 * Sequential checkpoint tracking + split times.
 *
 * Pure logic (no three.js, no DOM) so it is straightforward to unit-test, and
 * so it can later be reused server-side to validate submitted runs: a run that
 * reports a finish time without plausible splits is not a run.
 */

import type { Vec3 } from '../utils/math';
import { boxContainsXZ, type CheckpointId, type CheckpointSpec } from './Level';

export interface Split {
  id: CheckpointId;
  label: string;
  /** Run time in milliseconds when this checkpoint was first reached. */
  timeMs: number;
}

export class CheckpointTracker {
  private readonly splits: Split[] = [];
  private index = 0;

  constructor(private readonly specs: readonly CheckpointSpec[]) {}

  reset(): void {
    this.splits.length = 0;
    this.index = 0;
  }

  get reached(): readonly Split[] {
    return this.splits;
  }

  get nextCheckpoint(): CheckpointSpec | null {
    return this.specs[this.index] ?? null;
  }

  get isComplete(): boolean {
    return this.index >= this.specs.length;
  }

  /**
   * Tests every not-yet-reached volume. If the player skips one (an unexpected
   * shortcut, or clipping past a trigger at high speed) the skipped checkpoints
   * are recorded at the same time rather than stalling progression forever.
   *
   * @returns the newly reached checkpoints, in order.
   */
  update(feet: Vec3, timeMs: number): Split[] {
    const gained: Split[] = [];
    for (let i = this.index; i < this.specs.length; i++) {
      const spec = this.specs[i];
      if (!spec.box) continue;
      if (!boxContainsXZ(spec.box, feet.x, feet.y, feet.z)) continue;
      for (let j = this.index; j <= i; j++) {
        gained.push(this.record(this.specs[j], timeMs));
      }
      this.index = i + 1;
      break;
    }
    return gained;
  }

  /** Records action checkpoints, including sitting down and completing the PIN. */
  complete(id: CheckpointId, timeMs: number): Split[] {
    const target = this.specs.findIndex((spec) => spec.id === id);
    if (target < 0 || target < this.index) return [];
    const gained: Split[] = [];
    for (let i = this.index; i <= target; i++) {
      gained.push(this.record(this.specs[i], timeMs));
    }
    this.index = target + 1;
    return gained;
  }

  private record(spec: CheckpointSpec, timeMs: number): Split {
    const split: Split = { id: spec.id, label: spec.label, timeMs };
    this.splits.push(split);
    return split;
  }
}
